import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle, XCircle, CreditCard, Plus, User } from 'lucide-react';
import { subscribeToBookings } from '../../services/firestore';
import { BOOKING_STATUS } from '../../types/booking';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';

const RecentActivity = ({ limit = 10 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToBookings((bookings) => {
      // Transform bookings into activity feed
      const activityList = [];

      bookings.forEach(booking => {
        // Created activity
        if (booking.createdAt) {
          activityList.push({
            id: `${booking.id}-created`,
            type: 'created',
            bookingId: booking.id,
            bookingNumber: booking.bookingNumber,
            clientName: booking.clientName,
            timestamp: booking.createdAt,
            icon: Plus,
            iconColor: 'text-blue-400',
            iconBg: 'bg-blue-500/20',
            description: `Booking baru dibuat oleh ${booking.clientName}`,
          });
        }

        // Status change activities
        if (booking.statusHistory && booking.statusHistory.length > 0) {
          booking.statusHistory.forEach((history, index) => {
            if (index === 0) return; // Skip first entry as it's the creation

            let icon, iconColor, iconBg, description;

            switch (history.status) {
              case BOOKING_STATUS.CONFIRMED:
                icon = CheckCircle;
                iconColor = 'text-green-400';
                iconBg = 'bg-green-500/20';
                description = `Booking ${booking.bookingNumber} dikonfirmasi`;
                break;
              case BOOKING_STATUS.CANCELLED:
                icon = XCircle;
                iconColor = 'text-red-400';
                iconBg = 'bg-red-500/20';
                description = `Booking ${booking.bookingNumber} dibatalkan`;
                break;
              case BOOKING_STATUS.COMPLETED:
                icon = CheckCircle;
                iconColor = 'text-emerald-400';
                iconBg = 'bg-emerald-500/20';
                description = `Booking ${booking.bookingNumber} selesai`;
                break;
              default:
                icon = Calendar;
                iconColor = 'text-[#D4AF7A]';
                iconBg = 'bg-[#D4AF7A]/20';
                description = `Status booking ${booking.bookingNumber} berubah`;
            }

            activityList.push({
              id: `${booking.id}-status-${index}`,
              type: 'status_change',
              bookingId: booking.id,
              bookingNumber: booking.bookingNumber,
              clientName: booking.clientName,
              timestamp: history.timestamp,
              reason: history.reason,
              changedBy: history.changedBy,
              icon,
              iconColor,
              iconBg,
              description,
            });
          });
        }

        // Payment activities
        if (booking.payments && booking.payments.length > 0) {
          booking.payments.forEach((payment, index) => {
            if (payment.createdAt) {
              activityList.push({
                id: `${booking.id}-payment-${payment.id || index}`,
                type: 'payment',
                bookingId: booking.id,
                bookingNumber: booking.bookingNumber,
                clientName: booking.clientName,
                timestamp: payment.createdAt,
                amount: payment.amount,
                paymentMethod: payment.paymentMethod,
                icon: CreditCard,
                iconColor: 'text-purple-400',
                iconBg: 'bg-purple-500/20',
                description: `Pembayaran ${payment.paymentType || ''} diterima untuk ${booking.bookingNumber}`,
              });
            }
          });
        }
      });

      // Sort by timestamp (newest first)
      activityList.sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return timeB - timeA;
      });

      // Limit results
      setActivities(activityList.slice(0, limit));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [limit]);

  const formatTime = (timestamp) => {
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true, locale: id });
    } catch {
      return 'Baru saja';
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">Aktivitas Terbaru</h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-[#D4AF7A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">Aktivitas Terbaru</h3>
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-[#D4AF7A]/50 mx-auto mb-3" />
          <p className="text-[#F7F5F2]/70">Belum ada aktivitas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#F7F5F2]">Aktivitas Terbaru</h3>
        <span className="text-xs text-[#F7F5F2]/50">{activities.length} aktivitas</span>
      </div>

      <div className="space-y-4">
        {activities.map((activity) => {
          const Icon = activity.icon;
          return (
            <div
              key={activity.id}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
              onClick={() => {
                // Navigate to booking details
                if (activity.bookingId) {
                  window.location.href = `/admin/bookings?booking=${activity.bookingId}`;
                }
              }}
            >
              <div className={`w-10 h-10 rounded-lg ${activity.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-5 h-5 ${activity.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#F7F5F2] group-hover:text-[#D4AF7A] transition-colors">
                  {activity.description}
                </p>
                {activity.reason && (
                  <p className="text-xs text-[#F7F5F2]/60 mt-1">
                    Alasan: {activity.reason}
                  </p>
                )}
                {activity.amount && (
                  <p className="text-xs text-[#D4AF7A] mt-1 font-medium">
                    {activity.paymentMethod} • {new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                      minimumFractionDigits: 0
                    }).format(activity.amount)}
                  </p>
                )}
                <p className="text-xs text-[#F7F5F2]/50 mt-1">
                  {formatTime(activity.timestamp)}
                  {activity.changedBy && ` • ${activity.changedBy}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivity;
